// src/components/inputs/MultipleSelect.jsx

import React, { useMemo, useState } from 'react';
import {
    Box,
    Chip,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
    ListSubheader,
    TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const MultipleSelect = ({
    name,
    selectedValue = [],
    handleSelectChange,
    multiOptions,
    loading,
    initialMemberIds = [],
    sx,
    showChips = true,
}) => {
    const [search, setSearch] = useState('');

    // ✅ دالة جلب الاسم بشكل آمن (تدعم النصوص والكائنات)
    const getOptionLabel = (opt) => {
        if (!opt?.name) return "";
        if (typeof opt.name === "string") return opt.name;
        if (typeof opt.name === "object") {
            return opt.name.ar || opt.name.en || "";
        }
        return "";
    };

    // فلترة الأوبشنز حسب السيرش وإخفاء القيم المحددة
    const filteredOptions = useMemo(() => {
        const selectedIds = (selectedValue || []).map((v) => String(v));
        const availableOptions = (multiOptions || []).filter((opt) => !selectedIds.includes(String(opt.id)));
        if (!search) return availableOptions;
        const lower = search.toLowerCase();
        return availableOptions.filter((opt) => {
            const label = getOptionLabel(opt);
            return String(label).toLowerCase().includes(lower);
        });
    }, [multiOptions, search, selectedValue]);

    // إزالة أي قيمة من selectedValue عبر الـ Chip
    const handleChipDelete = (valueToDelete) => {
        const newSelected = selectedValue.filter((val) => val !== valueToDelete);
        const syntheticEvent = {
            target: {
                name,
                value: newSelected,
                selectedOptions: newSelected.map((id) => ({ value: id })),
                multiple: true,
            },
        };
        handleSelectChange(syntheticEvent);
        setSearch('');
    };

    const handleSelectionChange = (event) => {
        setSearch('');
        handleSelectChange(event);
    };

    if (loading) return null;
    return (
        <Select
            name={name}
            multiple
            value={selectedValue}
            disabled={loading}
            onChange={handleSelectionChange}
            input={<OutlinedInput />}
            MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 320 } },
            }}
            // منع keyboard navigation الافتراضي
            onKeyDown={(e) => {
                // السماح فقط بـ Tab و Escape و Enter
                if (e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Enter') {
                    e.stopPropagation();
                }
            }}
            renderValue={(selected) => {
                const selectedLabels = (selected || []).map((val) => {
                    const option = multiOptions.find((o) => String(o.id) === String(val));
                    return getOptionLabel(option) || (typeof val === 'object' ? getOptionLabel(val) : String(val));
                }).filter(Boolean);

                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32, alignItems: 'center' }}>
                        {(!selected || selected.length === 0) ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ opacity: 0.7 }}
                            >
                                {/* Empty selected state */}
                            </Typography>
                        ) : showChips ? (
                            selected.map((val) => {
                                const emp = multiOptions.find(
                                    (o) => String(o.id) === String(val)
                                );
                                return (
                                    <Chip
                                        key={val}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        label={getOptionLabel(emp) || (typeof val === 'object' ? getOptionLabel(val) : val)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onDelete={() => handleChipDelete(val)}
                                        sx={{
                                            maxWidth: 160,
                                            '& .MuiChip-label': {
                                                display: 'block',
                                            },
                                        }}
                                    />
                                );
                            })
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: '100%',
                                }}
                            >
                                {selectedLabels.join(', ')}
                            </Typography>
                        )}
                    </Box>
                );
            }}
            sx={{
                width: '100%',
                mt: 1,
                backgroundColor: (theme) => theme.palette.background.input,
                borderRadius: 1,
                minWidth: '80px',
                minHeight: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                },
                '& .MuiSelect-icon': {
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: 8,
                    color: 'text.secondary',
                    fontSize: 20,
                },
                ...sx,
            }}
        >
            {/* حقل السيرش داخل القائمة */}
            <ListSubheader
                sx={{ px: 1, pb: 1, pt: 1 }}
                onKeyDown={(e) => {
                    // منع إغلاق القائمة أو التنقل عند الكتابة
                    e.stopPropagation();
                }}
            >
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        // منع إغلاق القائمة عند الضغط على أي مفتاح
                        e.stopPropagation();
                    }}
                    InputProps={{
                        sx: {
                            fontSize: 13,
                            '& .MuiOutlinedInput-input': { py: 0.6 },
                        },
                    }}
                />
            </ListSubheader>

            {filteredOptions?.length === 0 && (
                <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                        لا توجد نتائج
                    </Typography>
                </MenuItem>
            )}

            {filteredOptions?.map((option) => {
                const isSelected = selectedValue.some(
                    (v) => String(v) === String(option.id)
                );
                return (
                    <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography variant="body2">{getOptionLabel(option)}</Typography>
                        {isSelected && (
                            <CheckIcon
                                fontSize="small"
                                color="primary"
                                sx={{ ml: 1, flexShrink: 0 }}
                            />
                        )}
                    </MenuItem>
                );
            })}
        </Select>
    );
};

export default MultipleSelect;
